<template>
  <div class="app-container">
    <Container
      :title="t('pay.payList')"
      :hasLine="true"
    >
      <template #header>
        <SearchForm
          :formModel="searchFormModel"
          :formStruct="searchFormStruct"
          :hasCreate="false"
          :submitButton="t('common.search')"
          :resetButton="t('common.reset')"
          @submit="submitSearch"
          @reset="resetForm"
        />
      </template>
      <template #main>
        <div>
          <el-dropdown
            v-if="$hasPermission(['PAYMENT_PLAN_W'])"
            class="avatar-container right-menu-item hover-effect"
            trigger="click"
          >
            <div class="avatar-wrapper">
              <el-button
                type="primary"
                size="large"
                style="margin-bottom: 10px"
                @click="handlePaymentOperation"
              >
                {{ t('pay.paymentOperation') }}
              </el-button>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item
                  @click="setPaymentDateItemClick"
                >
                  <span style="display:block;">
                    {{ t('pay.setPaymentDate') }}
                  </span>
                </el-dropdown-item>
                <el-dropdown-item
                  divided
                  @click="viewAndPayItemClick"
                >
                  <span style="display:block;">
                    {{ t('pay.viewAndPay') }}
                  </span>
                </el-dropdown-item>
                <el-dropdown-item
                  divided
                  @click="cancelPayItemClick"
                >
                  <span style="display:block;">
                    {{ t('pay.cancelPay') }}
                  </span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
        <SimpleTable
          ref="tableRef"
          :listLoading="listLoading"
          :tableData="tableData"
          :columnStruct="columnStruct"
          :hadSelect="true"
          :operateColumnWidth="150"
          :row-class-name="tableRowClassName"
        >
          <template
            #default="slotProps"
          >
            <el-button
              v-if="$hasPermission(['PAYMENT_PLAN_W']) && slotProps.row.isForeignCurrency"
              @click="handleExamine(slotProps.row)"
              type="primary"
              size="mini"
            >
              {{ t('pay.confirmVitro') }}
            </el-button>
          </template>
        </SimpleTable>
        <el-pagination
          :total="total"
          v-show="total > 0"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :page-size="limit"
          :current-page="page"
          :page-sizes="[10, 20, 50, 100, 200]"
          layout="total, sizes, prev, pager, next, jumper"
          style="text-align: right;"
        />
      </template>
    </Container>
    <BaseDialog
      v-model="datePickerDialogVisible"
      :title="t('order.setPaymentDate')"
      :autoClose="false"
      width="30%"
      content-center
      destroy-on-close
      @confirm="onDatePickerDialogConfirm"
    >
      <template #default>
        <el-date-picker
          v-model="selectedDate"
          type="date"
          :placeholder="t('common.selectDateTip')"
          :default-value="new Date()"
          :show-time="false"
          :size="large"
        />
      </template>
    </BaseDialog>
    <el-dialog
      width="40%"
      v-model="dialogCancelFormVisible"
    >
      <div
        style="text-align: center; padding-bottom: 30px;"
      >
        <i
          style="font-size: 40px; color:#ff4949; padding-bottom: 30px;"
          class="el-icon-warning"
        /><br>
        <span>{{ t('pay.cancelTip') }}{{ t('pay.cancelConfirmTip') }}</span>
      </div>
      <div
        class="dialog-footer"
        style="text-align: right;"
      >
        <el-button @click="dialogCancelFormVisible = false">
          {{ t('table.cancel') }}
        </el-button>
        <el-button
          type="primary"
          @click="handleCancelSelect"
        >
          {{ t('table.confirm') }}
        </el-button>
      </div>
    </el-dialog>
    <VitroPayDialog
      v-if="vitroPayDialogVisible"
      v-model="vitroPayDialogVisible"
      :vitroData="vitroData"
      @refresh="onVitroPayDialogConfirm"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, onActivated, onMounted, reactive, ref, toRefs } from 'vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import Container from '@/components/form/container/Container.vue'
import { useI18n } from 'vue-i18n'
import { createModel } from '@/utils/component'
import {
  PaymentSlipModel,
  PaymentSlipPageParamModel,
  PaymentSlipIdListModel
} from '@/model/paymentSlip/paymentSlipModel'
import { formatDate, formatThousands } from '@/utils'
import {
  getPaymentSlipPageList,
  setPaymentSlipPaymentDate,
  cancelPaymentSlipApplication,
  getWorking,
  viewAndPay
} from '@/apis/paymentSlip/paymentSlip'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import { ElMessage } from 'element-plus'
import {
  SAPRequestIdListModel,
  SAPRequestSetPaymentDateModel
} from '@/model/application-form/paymentRequestSap'
import BaseDialog from '@/components/dialog/BaseDialog.vue'
import VitroPayDialog from '@/views/pay/VitroPayDialog.vue'
import { useRouter } from 'vue-router'
import dayjs from 'dayjs'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { getCompanyAllList } from '@/apis/main-data/company'

export default defineComponent({
  name: 'PaymentSlipPageList',
  components: {
    BaseDialog,
    SearchForm,
    SimpleTable,
    Container,
    VitroPayDialog
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const tableRef = ref()
    const refresh = ref(false)
    const selectedRows = ref<PaymentSlipModel[]>()
    const searchStruct = [
      {
        prop: 'companyCode',
        label: t('company.companyCode'),
        component: 'el-select',
        type: 'text',
        model: '',
        filterable: true,
        loading: false,
        options: [] as any
      },
      { prop: 'payee', label: t('pay.payee'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'dueDate',
        label: t('order.cutOffDate'),
        component: 'el-date-picker',
        type: 'date',
        model: '',
        suffix: '前'
      },
      { prop: 'currency', label: t('pay.currency'), component: 'el-input', type: 'text', model: '' }
    ]
    const paymentSlipTableStruct = [
      { prop: 'id', label: t('pay.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'payee', label: t('pay.payee') },
      { prop: 'reference', label: t('pay.reference') },
      { prop: 'amount', label: t('pay.amount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      { prop: 'currency', label: t('pay.currency') },
      { prop: 'dueDate', label: t('order.cutOffDate'), formatter: (row: any) => formatDate(row.dueDate, 'yyyy-MM-dd') },
      { prop: 'comments', label: t('pay.comments') },
      {
        prop: 'paymentDate',
        label: t('pay.paymentDate'),
        formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd')
      },
      { prop: 'purpose', label: t('pay.purposeView'), width: 170 }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as PaymentSlipPageParamModel,
      tableData: Array<PaymentSlipModel>(),
      columnStruct: paymentSlipTableStruct,
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      dialogShowPaymentSlip: false,
      datePickerDialogVisible: false,
      selectedDate: null as Date | null,
      dialogCancelFormVisible: false,
      vitroPayDialogVisible: false,
      dialogFormVisible: false,
      vitroData: null as PaymentSlipModel | null,
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }
        dataMap.listLoading = true
        let dueDate = null
        if (dataMap.searchFormModel.dueDate) {
          dueDate = dayjs(dataMap.searchFormModel.dueDate).endOf('day').format('YYYY-MM-DD HH:mm:ss SSS')
        }
        const params: PaymentSlipPageParamModel = {
          companyCode: dataMap.searchFormModel.companyCode,
          payee: dataMap.searchFormModel.payee,
          dueDate: dueDate,
          currency: dataMap.searchFormModel.currency,
          isForeignCurrency: null,
          comment: null,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        const data = await getPaymentSlipPageList(params)
        if (data && data.data && data.data.list.length > 0) {
          dataMap.tableData = data.data.list
        } else {
          dataMap.tableData = []
        }
        dataMap.total = data?.data.totalCount ?? 0
        tableRef.value.clearSelection()
        refresh.value = false
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      }
    })
    const handlePaymentOperation = () => {
      // dataMap.dialogShowPaymentSlip = true
    }
    const setPaymentDateItemClick = () => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        dataMap.selectedDate = null
        dataMap.datePickerDialogVisible = true
      } else {
        ElMessage({
          message: t('pay.selectTip'),
          type: 'error'
        })
      }
    }
    const onDatePickerDialogConfirm = async() => {
      if (dataMap.selectedDate != null) {
        const params: SAPRequestSetPaymentDateModel = {
          idList: tableRef.value.getSelectionRows().map((row: any) => row.id),
          paymentDate: formatDate(dataMap.selectedDate)
        }
        const res = await setPaymentSlipPaymentDate(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.setSuccess'),
            type: 'success',
            duration: 2000
          })
          dataMap.datePickerDialogVisible = false
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
        await dataMap.getPageList(dataMap.page, dataMap.limit)
      } else {
        ElMessage({
          message: t('order.setPaymentDateP'),
          type: 'error'
        })
      }
    }
    const viewAndPayItemClick = async() => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        if (tableRef.value.getSelectionRows().some((row: any) => row.isForeignCurrency)) {
          ElMessage({
            message: t('pay.selectForeignCurrencyTip'),
            type: 'error'
          })
          return
        }
        const bo: PaymentSlipIdListModel = {
          idList: tableRef.value.getSelectionRows().map((row: any) => row.id)
        }
        const res = await viewAndPay(bo)
        if (!res?.data) {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
          return
        }
        refresh.value = true
      } else {
        const res = await getWorking()
        if (res?.data) {
          if (res.data.length < 1) {
            ElMessage({
              message: t('pay.selectTip'),
              type: 'error'
            })
            return
          }
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
          return
        }
      }
      await router.push({ path: 'paymentSlipDetail' })
    }
    const cancelPayItemClick = () => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        dataMap.dialogCancelFormVisible = true
      } else {
        ElMessage({
          message: t('pay.selectTip'),
          type: 'error'
        })
      }
    }
    const handleCancelSelect = async() => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        const params: SAPRequestIdListModel = { idList: tableRef.value.getSelectionRows().map((row: any) => row.id) }
        const res = await cancelPaymentSlipApplication(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.cancelSuccess'),
            type: 'success',
            duration: 2000
          })
          await dataMap.getPageList(dataMap.page, dataMap.limit)
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      } else {
        ElMessage({
          message: t('pay.selectTip'),
          type: 'error'
        })
      }
      dataMap.dialogCancelFormVisible = false
    }
    const handleExamine = (row: PaymentSlipModel) => {
      // router.push({ path: `approvalFormDetail/${row.id}` })
      dataMap.vitroData = row
      dataMap.vitroPayDialogVisible = true
    }
    const onVitroPayDialogConfirm = async() => {
      await dataMap.getPageList(null, 10)
    }
    const tableRowClassName = ({ row }: { row: PaymentSlipModel }) => {
      if (row.paymentDate !== null) {
        // 复制 Date 对象，避免修改原对象
        const paymentDate = new Date(row.paymentDate).setHours(0, 0, 0, 0)
        const nowDateTime = new Date().setHours(0, 0, 0, 0)
        if (paymentDate <= nowDateTime) {
          return 'warning-row'
        }
        return ''
      }
      return ''
    }
    const getCompanyList = async() => {
      dataMap.searchFormStruct[0].loading = true
      const params: CompanyListParamModel = {
        name: null,
        companyCode: null
      }
      const data = await getCompanyAllList(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({
          label: iterator.companyCode + ', ' + iterator.name,
          value: iterator.companyCode
        })
      }
      dataMap.searchFormStruct[0].options = optionList
      dataMap.searchFormStruct[0].loading = false
    }
    onMounted(() => {
      dataMap.getPageList(null, 10)
      getCompanyList()
    })
    onActivated(async() => {
      if (refresh.value) {
        await dataMap.getPageList()
      }
    })
    return {
      t,
      tableRef,
      selectedRows,
      handlePaymentOperation,
      setPaymentDateItemClick,
      onDatePickerDialogConfirm,
      viewAndPayItemClick,
      cancelPayItemClick,
      handleCancelSelect,
      handleExamine,
      onVitroPayDialogConfirm,
      tableRowClassName,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style
  scoped
  lang="scss"
>

</style>
